import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "../styles/EthnicityAnalysis.css";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function TableNo15() {
  const pdfRef = useRef();

  // 📊 Dataset
  const ethnicityData = [
    {
      ethnicity: "Hispanic or Latino",
      share2018: 18.69,
      share2019: 19.07,
      share2020: 19.4,
      share2021: 19.45,
      share2022: 20.16,
      share2023: 20.7,
      share2024: 21.54,
    },
    {
      ethnicity: "Not Hispanic or Latino",
      share2018: 81.31,
      share2019: 80.93,
      share2020: 80.6,
      share2021: 80.55,
      share2022: 79.84,
      share2023: 79.3,
      share2024: 78.46,
    },
  ];

  // 🧮 Growth Calculation
  const dataWithChange = ethnicityData.map((item) => ({
    ...item,
    change: (item.share2024 - item.share2018).toFixed(2),
  }));

  const highest = dataWithChange.reduce((a, b) =>
    parseFloat(a.change) > parseFloat(b.change) ? a : b,
  );
  const lowest = dataWithChange.reduce((a, b) =>
    parseFloat(a.change) < parseFloat(b.change) ? a : b,
  );

  // 🎨 Color Palette by Year
  const yearColors = [
    "#FFF085",
    "#F6CFFF",
    "#2C92B8",
    "#9F9FA9",
    "#E9D4FF",
    "#D8F999",
    "#82181A",
  ];

  // 📈 Chart Data
  const chartData = {
    labels: ethnicityData.map((item) => item.ethnicity),
    datasets: Object.keys(ethnicityData[0])
      .filter((k) => k.startsWith("share"))
      .map((key, i) => ({
        label: key.replace("share", "Year "),
        data: ethnicityData.map((row) => row[key]),
        backgroundColor: yearColors[i],
        borderRadius: 6,
      })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Ethnicity Share Comparison (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toFixed(2)}%`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: "Percentage Share",
          font: { size: 14, weight: "bold" },
        },
        ticks: { callback: (v) => v + "%" },
      },
      x: {
        ticks: { color: "#374151", font: { size: 12 } },
        title: {
          display: true,
          text: "Ethnicity Groups",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // 📄 PDF Export
    const downloadPDF = () => {
      const input = pdfRef.current;
      html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;
        let pageNumber = 1;

        const addHeader = () => {
          const logoWidth = 20; // ideal header width
          const logoHeight = 20; // adjust ratio if needed

          pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
          pdf.setFontSize(26);
          pdf.text("UTahData.org", 95, 22);
          pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
          pdf.line(10, 35, 200, 35);
        };

        const addFooter = (pageNumber) => {
          pdf.setFontSize(9);
          const footerY = pageHeight - 10;
          pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
          pdf.text(
            `Downloaded on: ${new Date().toLocaleDateString()}`,
            10,
            footerY,
          );
          pdf.text(
            `Page ${pageNumber}`,
            pageWidth - 10,
            footerY,
            null,
            null,
            "right",
          );
        };

        // First page
        addHeader();
        pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;

        // Add more pages if needed
        while (heightLeft > 0) {
          pdf.addPage();
          pageNumber++;
          addHeader();
          position = heightLeft - imgHeight;
          pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
          heightLeft -= pageHeight - 32;
          addFooter(pageNumber);
        }

        // Final footer
        // ===== FINAL FOOTER SAFE AREA =====

        const footerTopY = pageHeight - 40; // start footer 40mm from bottom
        const footerContentY = pageHeight - 28; // logos row
        const footerBottomTextY = pageHeight - 10; // bottom text row

        // Top Divider Line
        pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

        // UtahData logo
        const utahWidth = 18;
        const utahHeight = 18;
        pdf.addImage(
          utahData,
          "PNG",
          20,
          footerContentY - 8,
          utahWidth,
          utahHeight,
        );

        // Built by
        pdf.setFontSize(10);
        pdf.text("Built by", 45, footerContentY);

        // BODEX logo
        const bodexWidth = 36;
        const bodexHeight = bodexWidth * 0.3;
        pdf.addImage(
          bodex,
          "PNG",
          60,
          footerContentY - 6,
          bodexWidth,
          bodexHeight,
        );

        // Using text
        pdf.text("using", 102, footerContentY);

        // KPI logo
        pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

        // Version
        pdf.text(APP_VERSION, 140, footerContentY);

        // Bottom Info Line
        pdf.setFontSize(8);
        pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          pageWidth - 10,
          footerBottomTextY,
          null,
          null,
          "right",
        );
        pdf.save("ethnicity_share_comparison.pdf");
        // input.style.width = originalWidth;
      });
    };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-pink-50 rounded-xl shadow-lg"
      >
        {/* Header */}
        <h2 className="text-2xl font-bold mb-4 text-center text-gray-800">
          Ethnicity Share Comparison (2018–2024)
        </h2>
        <p className="text-center text-gray-600 mb-6">
          This chart highlights the change in Hispanic or Latino versus Not
          Hispanic or Latino workforce shares between 2018 and 2024.
        </p>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-center">
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Highest Growth</p>
            <p className="text-lg font-bold text-green-700">
              {highest.ethnicity} (+{highest.change}%)
            </p>
          </div>
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Largest Decline</p>
            <p className="text-lg font-bold text-red-700">
              {lowest.ethnicity} ({lowest.change}%)
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-blue-700 text-white">
                <tr>
                  <th className="p-2 text-left">Ethnicity</th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2018
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2019
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2020
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2021
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2022
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2023
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2024
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    Δ (2018–2024)
                  </th>
                </tr>
              </thead>
              <tbody>
                {dataWithChange.map((item, i) => (
                  <tr
                    key={i}
                    className={`hover:bg-gray-100 transition ${
                      item.change > 0 ? "bg-green-50" : "bg-red-50"
                    }`}
                  >
                    <td className="p-2 font-medium text-left">
                      {item.ethnicity}
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {item.share2018.toFixed(2)}%
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {item.share2019.toFixed(2)}%
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {item.share2020.toFixed(2)}%
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {item.share2021.toFixed(2)}%
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {item.share2022.toFixed(2)}%
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {item.share2023.toFixed(2)}%
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {item.share2024.toFixed(2)}%
                    </td>
                    <td
                      className={`p-2 font-semibold ${
                        item.change > 0 ? "text-green-700" : "text-red-700"
                      }`}
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {item.change > 0 ? "+" : ""}
                      {item.change}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
            <div
              className="x-axis-legend"
              style={{
                marginTop: "10px",
                padding: "10px",
                backgroundColor: "#f9fafb",
                borderLeft: "4px solid #014cb2",
                fontSize: "14px",
                color: "#6b7280",
                marginLeft: "16px",
                marginRight: "16px",
                marginBottom: "16px",
              }}
            >
              <strong>X-axis Legend:</strong> The chart displays ethnicity
              groups (Hispanic or Latino, Not Hispanic or Latino) on the x-axis,
              with each bar representing the percentage share for a specific
              year from 2018 to 2024.
            </div>
          </div>
        </div>

        {/* Summary */}
        <p className="text-center text-gray-600">
          📊 Between 2018 and 2024,{" "}
          <span className="font-semibold text-green-700">
            {highest.ethnicity}
          </span>{" "}
          increased its share by {highest.change}%, while{" "}
          <span className="font-semibold text-red-700">{lowest.ethnicity}</span>{" "}
          declined by {Math.abs(lowest.change)}%.
        </p>
      </div>

      {/* Download PDF */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
